<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { MessageResultV2DTO } from '@nora-monorepo/nora-api-client';
import { formatName } from '@nora-monorepo/shared';
import {
  bgSecondary,
  textPrimary,
} from '@nora-monorepo/ui-shared';

import { urlGuardClickHandler } from '#nora-chat-ui/directives/url-guard/url-guard.directive';
import type { MessageState } from '#nora-chat-ui/states';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';
import { renderMessagePreview } from '#nora-chat-ui/utils/message-markdown';

interface MessageQuoteInputProps {
  messageState: MessageState;
}

interface MessageQuoteInputEmits {
  (event: 'close'): void;
}

const props = defineProps<MessageQuoteInputProps>();
const emit = defineEmits<MessageQuoteInputEmits>();

const conversationMessageStore = useConversationMessageStore();

const { t } = useI18n();
const message = computed<MessageResultV2DTO>(() => props.messageState.message);
const messagePreview = computed(() =>
  renderMessagePreview(message.value.message ?? ''),
);

const { quoteMessage } = storeToRefs(conversationMessageStore);

function close() {
  if (quoteMessage.value) {
    quoteMessage.value = undefined;

    emit('close');
  }
}

function onMessagePreviewClick(event: MouseEvent): void {
  if (!(event.target instanceof Element)) {
    return;
  }

  const link = event.target.closest('a');
  if (link) {
    event.stopPropagation();
    urlGuardClickHandler(link, event);
  }
}
</script>

<template>
  <div class="flex justify-between p-3" :class="[bgSecondary, textPrimary]">
    <div class="max-w-full">
      <div class="font-semibold">
        {{ t('conversation.replyingTo', [formatName(message.sentBy)]) }}
      </div>
      <span v-if="message.attachments?.length">{{ message.attachments.length }}
        {{ t('attachments.attachment') }}</span>
      <div v-else class="break-all line-clamp-2 markdown" @click="onMessagePreviewClick">
        <!-- eslint-disable vue/no-v-html -->
        <span v-html="messagePreview" />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </div>
    <a class="cursor-pointer" @click="close"><span class="mi close" /></a>
  </div>
</template>
